import { useState, useEffect, lazy, Suspense } from 'react';
import { UserProvider, useUser, QUICK_LOGIN_CREDS } from './context/user-context';
import { TablesProvider } from './context/tables-context';
import { BranchProvider, useBranch } from './context/branch-context';
import { LoggingProvider, useLogging } from './context/logging-context';
import { UiAlertProvider } from './context/ui-alert-context';
import { SearchProvider } from './context/search-context';
import { RegionalProvider, useRegional, useTextos } from './context/regional-context';
import { textosComun } from '../i18n/modulos/comun';
import { SidebarCool } from './components/sidebar-cool';
import { TopBarCool } from './components/top-bar-cool';
import { AvisoSinConexion } from './components/indicador-conexion';
import { CopiaLocalContingencia } from './components/copia-local-contingencia';
import { CajaContingencia } from './components/contingencia/caja-contingencia';
import { SubidaContingencia } from './components/contingencia/subida-contingencia';
import { MobileTabBarCool } from './components/mobile-tab-bar-cool';
import { useNotifications, clearNotificationsDismissed } from './hooks/use-notifications';
import { useAprobacionesBadge } from './hooks/use-aprobaciones-badge';
import { useAppFlags } from './hooks/use-app-flags';

// =============================================================================
// CODE SPLITTING — cada módulo es un chunk separado que se carga bajo demanda.
// Reduce el bundle inicial (~1.2MB) a ~300KB y el LCP del primer paint.
// Solo Login + Dashboard se cargan eagerly porque son los puntos de entrada
// más comunes (login → dashboard al loguear).
// =============================================================================
import { Login } from './components/login';
import { Dashboard } from './components/dashboard';

const Kpis            = lazy(() => import('./components/kpis').then(m => ({ default: m.Kpis })));
const Orders          = lazy(() => import('./components/orders').then(m => ({ default: m.Orders })));
const Reservations    = lazy(() => import('./components/reservations').then(m => ({ default: m.Reservations })));
const Tables          = lazy(() => import('./components/tables').then(m => ({ default: m.Tables })));
const Inventory       = lazy(() => import('./components/inventory').then(m => ({ default: m.Inventory })));
const Purchases       = lazy(() => import('./components/purchases').then(m => ({ default: m.Purchases })));
const Contabilidad    = lazy(() => import('./components/contabilidad').then(m => ({ default: m.Contabilidad })));
const Users           = lazy(() => import('./components/users').then(m => ({ default: m.Users })));
const Terceros        = lazy(() => import('./components/terceros').then(m => ({ default: m.Terceros })));
const Reports         = lazy(() => import('./components/reports').then(m => ({ default: m.Reports })));
const Menu            = lazy(() => import('./components/menu').then(m => ({ default: m.Menu })));
// Combos (mig. 049). Vive al lado de Carta porque un combo ES un producto:
// tiene código, precio e INC propios y se vende como cualquier plato.
const Combos          = lazy(() => import('./components/combos').then(m => ({ default: m.Combos })));
const Params          = lazy(() => import('./components/params').then(m => ({ default: m.Params })));
const Billing         = lazy(() => import('./components/billing').then(m => ({ default: m.Billing })));
const LegalEntities   = lazy(() => import('./components/legal-entities').then(m => ({ default: m.LegalEntities })));
const PaymentsScreen  = lazy(() => import('./components/payments/payments-screen').then(m => ({ default: m.PaymentsScreen })));
const DeveloperLogs   = lazy(() => import('./components/developer-logs').then(m => ({ default: m.DeveloperLogs })));
const TechnicalDocs   = lazy(() => import('./components/technical-docs').then(m => ({ default: m.TechnicalDocs })));
const Features        = lazy(() => import('./components/features').then(m => ({ default: m.Features })));
const EnviosMU        = lazy(() => import('./components/envios-mu').then(m => ({ default: m.EnviosMU })));
const CredencialesScr = lazy(() => import('./components/credenciales').then(m => ({ default: m.Credenciales })));
const Notificaciones  = lazy(() => import('./components/notificaciones').then(m => ({ default: m.Notificaciones })));
const Activos         = lazy(() => import('./components/activos').then(m => ({ default: m.Activos })));
// Aprobaciones (mig. 058): lo que el cajero pide y el admin de sede aprueba.
const Aprobaciones    = lazy(() => import('./components/aprobaciones').then(m => ({ default: m.Aprobaciones })));

// El archivo quick-order-modal.tsx se mantiene en el repo por ahora — algunos
// componentes legacy (tables.tsx en su flujo "agregar items") lo siguen importando.
import { QuickReservationModal } from './components/quick-reservation-modal';
// UpcomingReservationsWidget removido (jun-2026) — las reservas próximas
// viven sólo en el bell de notificaciones.
import { ShoppingCart, Calendar, AlertTriangle } from 'lucide-react';
import { useLocation, useNavigate, Routes, Route } from 'react-router';
import { PublicFacturaForm } from './components/public-factura-form';
import { PublicCalificacionForm } from './components/public-calificacion-form';
import { Ratings } from './components/ratings';
import { Staff } from './components/staff';
import { Cortesias } from './components/cortesias';
import { resolveAppEnv } from '../lib/entorno';
import { AvisoPedidoWeb } from './components/aviso-pedido-web';

// Banner de entorno: se muestra en DEV y LOCAL, se OCULTA en producción.
// Fuente de verdad: VITE_APP_ENV (local|dev|prod), inyectada en el build
// (build-arg del Dockerfile / variable de compose). Si no está seteada, se
// infiere por hostname (compat hacia atrás): prod no lleva banner; `dev.` → DEV;
// cualquier otro (localhost/IP) → LOCAL. Evita confundir dev con prod al hacer
// ventas/facturas de prueba.
function DevBanner() {
  const env = resolveAppEnv();
  const tx = useTextos(textosComun);
  if (env === 'prod') return null;
  const label = env === 'dev' ? tx('entornoPruebasDev') : tx('entornoLocal');
  return (
    <div
      className="w-full flex items-center justify-center gap-2 px-3 py-1 shrink-0 text-center"
      style={{
        background: 'var(--warn-bg, #fde68a)',
        color: 'var(--warn-ink, #92400e)',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
      }}
    >
      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
      <span>{label}{tx('datosNoReales')}</span>
    </div>
  );
}

// Vistas válidas — espejean las URLs disponibles. Cualquier path fuera de
// esta lista cae al dashboard.
const VALID_VIEWS = new Set([
  'dashboard', 'kpis', 'orders', 'reservations', 'tables',
  'inventory', 'purchases', 'contabilidad', 'users',
  'reports', 'menu', 'combos', 'params', 'billing', 'payments',
  'legal-entities', 'debug', 'technical-docs', 'terceros',
  // Módulos con flag (se encienden desde Features vía app_settings):
  'ratings',  // Calificaciones (QR post-pago) — superadmin + desarrollador
  'staff',    // Personal (turnos de trabajadores)
  'cortesias', // Trackeo de cortesías (items regalados)
  'activos',        // Dotación y consumibles que NO son insumos de cocina
  'features',       // Flags + presupuesto IA + historial escaneos — SOLO dev
  'notificaciones', // Alertas, matriz de roles y correos — superadmin + dev
  'envios-mu',      // Domicilios con Mensajeros Urbanos: monitoreo + pedir servicio
  'credenciales',   // Llaves de Bold, MU, Anthropic, correo y WhatsApp por sede
  // Aprobaciones (mig. 058). Sin esta línea /aprobaciones caía en 'dashboard'
  // y el módulo se veía en el menú pero abría el Dashboard (bug 9-oct-2026).
  'aprobaciones',
]);

function AppContent() {
  // La URL es la fuente de verdad para activeView. Compartir un link tipo
  // /payments lleva directo al módulo. Botones del browser (back/forward)
  // funcionan solos.
  const location = useLocation();
  const navigate = useNavigate();
  const urlView = location.pathname.replace(/^\/+/, '').split('/')[0] || 'dashboard';
  const activeView = VALID_VIEWS.has(urlView) ? urlView : 'dashboard';
  const [showQuickReservation, setShowQuickReservation] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop: colapsa el sidebar a sólo iconos (favicon visible). Persistido en localStorage.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('salonika_sidebar_collapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('salonika_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  // Cerrar el drawer al cambiar a desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setSidebarOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { user, login, logout, loading, hasPermission, canViewModule } = useUser();
  const { currentBranch, currentBranchId, setCurrentBranch, branches } = useBranch();
  const { log } = useLogging();
  // ⚠ useNotifications debe ir ANTES de los early returns (loading / !user)
  // — si se llama después, React tira #310 "Rendered more hooks than during
  // the previous render" cuando el usuario hace logout/login.
  const notifs = useNotifications();
  // Flags de módulos opcionales (Features). Antes de los early returns.
  const flags = useAppFlags();
  // Moneda e idioma (oct-2026). Fuera de COP, Domicilios MU no se abre.
  const { t, operaColombia } = useRegional();
  const tx = useTextos(textosComun);
  // Mismo cuidado que useNotifications: antes de los early returns.
  const aprobacionesBadge = useAprobacionesBadge(!!user && canViewModule('aprobaciones') && user.role !== 'cajera');

  // Sincronizar sede cuando el usuario inicia sesión
  useEffect(() => {
    if (user && user.branch !== 'Todas las sedes') {
      setCurrentBranch(user.branch);
    }
  }, [user]);

  // Log cuando cambia la vista
  useEffect(() => {
    if (user) {
      log('info', 'navigation', 'VIEW_CHANGED', `Usuario navegó a: ${activeView}`, {
        view: activeView,
        previousView: activeView,
      });
      // Guardia: si llegó por URL a un módulo sin permiso, redirigir.
      if (!canViewModule(activeView)) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [activeView, user, canViewModule, navigate, log]);

  const handleLogin = async (username: string, password: string) => {
    try {
      await login(username, password);
      log('info', 'auth', 'LOGIN_SUCCESS', `Inicio de sesión exitoso: ${username}`, { username });
    } catch (err: any) {
      log('warning', 'auth', 'LOGIN_FAILED', `Intento de login fallido: ${username}`, {
        username,
        reason: err?.message || 'Credenciales incorrectas',
      });
      alert(err?.message || tx('usuarioOContrasenaIncorrectos'));
    }
  };

  const handleQuickLogin = async (userId: string) => {
    const creds = QUICK_LOGIN_CREDS[userId];
    if (!creds) {
      alert(tx('quickLoginNoConfigurado', { id: userId }));
      return;
    }
    await handleLogin(creds.username, creds.password);
  };

  const handleLogout = async () => {
    // En móvil/PWA standalone, window.confirm a veces no abre el diálogo y
    // bloquea el logout. Hacemos directo y cerramos el drawer del sidebar.
    log('info', 'auth', 'LOGOUT', `Usuario cerró sesión: ${user?.name}`, { userId: user?.id });
    // Las notificaciones dismissed se borran en logout — así reaparecen
    // al siguiente login si el problema sigue.
    clearNotificationsDismissed();
    await logout();
    navigate('/dashboard');
    setSidebarOpen(false);
  };

  // Navega a la URL del módulo. Esto actualiza activeView automáticamente
  // (porque deriva del location.pathname). El sidebar invoca esta función.
  const handleViewChange = (view: string) => {
    navigate(view === 'dashboard' ? '/' : `/${view}`);
    // Cerrar drawer en mobile después de elegir
    setSidebarOpen(false);
  };

  const handleBranchChange = (branch: string) => {
    log('info', 'navigation', 'BRANCH_CHANGED', `Cambio de sede: ${currentBranch} → ${branch}`, {
      previousBranch: currentBranch,
      newBranch: branch,
    });
    setCurrentBranch(branch);
  };

  // Splash mientras se restaura la sesión
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#fafafa]">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="w-2 h-2 rounded-full bg-primary/60 animate-pulse" />
          <span className="text-sm">{t('app.cargando')}</span>
        </div>
      </div>
    );
  }

  // Si no hay usuario logueado, mostrar login
  if (!user) {
    return <Login onLogin={handleLogin} onQuickLogin={handleQuickLogin} isDevelopment={true} />;
  }

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard branch={currentBranch} />;
      case 'kpis':
        return <Kpis />;
      case 'orders':
        return <Orders />;
      case 'reservations':
        return <Reservations />; 
      case 'tables':
        return <Tables />;
      case 'inventory':
        return <Inventory />; 
      case 'purchases':
        return <Purchases />;
      case 'contabilidad':
        return <Contabilidad />;
      case 'users':
        return <Users />;
      case 'terceros':
        return <Terceros />;
      case 'reports':
        return <Reports />; 
      case 'menu':
        return <Menu />; 
      case 'combos':
        // Apagado desde Features → no se entra ni por URL (oct-2026).
        return flags.combos ? <Combos /> : <Dashboard branch={currentBranch} />;
      case 'params':
        return <Params />; 
      case 'billing':
        return <Billing />;
      case 'legal-entities':
        return <LegalEntities />;
      case 'payments':
        return <PaymentsScreen />;
      case 'debug':
        return <DeveloperLogs />;
      case 'technical-docs':
        return <TechnicalDocs />;
      case 'ratings':
        return <Ratings />;
      case 'staff':
        return <Staff />;
      case 'cortesias':
        return <Cortesias />;
      case 'envios-mu':
        // Fuera de COP, Mensajeros Urbanos está desconectado (oct-2026).
        return operaColombia ? <EnviosMU /> : <Dashboard branch={currentBranch} />;
      case 'credenciales':
        return <CredencialesScr sedeId={currentBranchId} />;
      case 'features':
        return <Features />;
      case 'notificaciones':
        return <Notificaciones />;
      case 'activos':
        return <Activos />;
      case 'aprobaciones':
        return <Aprobaciones />;
      default:
        return <Dashboard branch={currentBranch} />;
    }
  };

  // Widget de próximas reservas: sólo para meseros. Los FABs flotantes
  // "Crear pedido"/"Nueva reserva" se eliminaron jun-2026 a pedido del
  // usuario — eran ruido visual permanente, los meseros entran al flujo
  // por la mesa o por el módulo correspondiente.
  // UpcomingReservationsWidget también se eliminó (jun-2026); las próximas
  // reservas (-10/+60 min) viven exclusivamente en el bell de notificaciones.
  // const isMesero = user.role === 'mesero';

  // ============================================================
  // Layout Cool — único shell de la app (jun-2026: eliminados classic,
  // alt y c por pedido del usuario). Shell "L" con hombro grande:
  // topbar 72px full-width sobre gradient azul + codo izquierdo con el
  // logo (236px), debajo sidebar 236px azul + contenido blanco con
  // border-radius 56px en la esquina superior-izquierda.
  // ============================================================
  return (
      <div
        className="flex flex-col h-screen"
        style={{ background: 'var(--blue-grad)' }}
      >
        <DevBanner />
        <AvisoPedidoWeb />
        <TopBarCool
          currentBranch={currentBranch}
          onBranchChange={handleBranchChange}
          branches={branches}
          onLogout={handleLogout}
          notifications={notifs.inventoryAlerts}
          onDismissNotification={notifs.dismiss}
          onDismissAllNotifications={notifs.dismissAll}
          onDismissReserva={notifs.dismissReserva}
          onGoToInventory={() => handleViewChange('inventory')}
          reservaAlerts={notifs.reservaAlerts}
          onGoToReservations={() => handleViewChange('reservations')}
          /* Tap en el logo mobile abre el drawer del sidebar (atajo a "Más"). */
          onMobileLogoTap={() => setSidebarOpen(true)}
          /* El codo del topbar se reduce a 72px cuando el sidebar está
             colapsado, para que el logo S quede alineado con los iconos. */
          sidebarCollapsed={sidebarCollapsed}
        />
        {/* Franja roja mientras el servidor no responda (modo contingencia). */}
        <AvisoSinConexion />
        {/* Copia de carta, mesas y pedidos para trabajar sin internet. No pinta nada. */}
        <CopiaLocalContingencia />
        {/* Caja de contingencia: pantalla aparte, encima de todo, solo cuando se abre. */}
        <CajaContingencia />
        {/* Sube lo de la caja de contingencia cuando hay conexión. No pinta nada. */}
        <SubidaContingencia />
        <div className="flex flex-1 min-h-0">
          <SidebarCool
            activeView={activeView}
            onViewChange={handleViewChange}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(c => !c)}
            badges={{
              orders:       notifs.badges.pedidos,
              inventory:    notifs.badges.inventario,
              reservations: notifs.badges.reservas,
              // undefined y no 0: el sidebar expandido pinta el badge aunque valga 0.
              aprobaciones: aprobacionesBadge || undefined,
            }}
          />
          {/* Content area — el hombro grande:
                · desktop: borde superior izquierdo a 56px (la C tradicional)
                · mobile:  ambos top corners a 34px (preserva el "gesto curvo"
                  del design responsive — no hay sidebar a la izquierda).
              Tailwind `rounded-t-[34px] lg:rounded-tl-[56px] lg:rounded-tr-none`
              hace el switch responsive sin media-queries manuales. */}
          <div
            className="flex-1 flex flex-col min-h-0 min-w-0 relative rounded-t-[34px] lg:rounded-tl-[56px] lg:rounded-tr-none"
            style={{
              background: 'var(--surface)',
              overflow: 'hidden',
            }}
          >
            {/* La búsqueda mobile vive ahora en el cluster del topbar como
                ícono de lupa (GlobalSearch iconOnly), no dentro del content.
                Pedido del usuario jun-2026 — el panel queda limpio.
                Padding removido del main (jun-2026): cada módulo ya aporta su
                propio padding interno (px-4 sm:px-6 lg:px-8 en módulos legacy
                o el padding del ModuleHeader). Esto da más espacio al
                contenido y elimina padding doble. */}
            <main className="flex-1 overflow-y-auto">
              <Suspense fallback={
                <div
                  className="flex items-center justify-center gap-3 h-full"
                  style={{ color: 'var(--muted-ink)', fontSize: 14, fontWeight: 600 }}
                >
                  <div
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      border: '2.5px solid var(--line)',
                      borderTopColor: 'var(--blue-600)',
                      animation: 'sk-spin 0.7s linear infinite',
                    }}
                  />
                  {t('app.cargandoModulo')}
                  <style>{`@keyframes sk-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              }>
                {renderView()}
              </Suspense>
            </main>
            {/* Bottom tab bar — sólo móvil. Cumple la doble función:
                navegar a favoritos + abrir el drawer del sidebar con "Más". */}
            <MobileTabBarCool
              activeView={activeView}
              onChange={handleViewChange}
              onMoreTap={() => setSidebarOpen(true)}
              badges={{
                orders:       notifs.badges.pedidos,
                inventory:    notifs.badges.inventario,
                reservations: notifs.badges.reservas,
              }}
            />
          </div>
        </div>

        {/* FABs eliminadas (jun-2026). Los modales se mantienen montados
            por si algún código futuro los dispara. */}
        {/* PosOrderModal eliminado (ago-2026). Los pedidos —de mesa y para
            llevar— se toman SIEMPRE en MesaWorkspace: una sola pantalla para
            agregar items, en vez de dos que había que mantener en paralelo (y
            la del modal ya se había quedado sin el toast de personalización).
            Con él se fue el estado `showQuickOrder`, que ya no disparaba nada. */}
        {showQuickReservation && (
          <QuickReservationModal
            isOpen={showQuickReservation}
            onClose={() => setShowQuickReservation(false)}
          />
        )}
      </div>
    );
}

// Shell autenticado (todos los providers + la app). Es el "resto" de rutas.
function AuthedApp() {
  return (
    <UserProvider>
      <BranchProvider>
        <SearchProvider>
          <TablesProvider>
            <LoggingProvider>
              <UiAlertProvider>
                <RegionalProvider>
                  <AppContent />
                </RegionalProvider>
              </UiAlertProvider>
            </LoggingProvider>
          </TablesProvider>
        </SearchProvider>
      </BranchProvider>
    </UserProvider>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Página pública (sin login): el cliente final llena sus datos de
          facturación. Fuera de todos los providers y del gate de auth. */}
      <Route path="/f/factura" element={<PublicFacturaForm />} />
      {/* Calificación del comensal — llega escaneando el QR post-pago. */}
      <Route path="/f/calificar/:token" element={<PublicCalificacionForm />} />
      <Route path="*" element={<AuthedApp />} />
    </Routes>
  );
}